// Workflow Hygiene Epic Linkage card. Renders the headline percent in the
// threshold-coloured tier, a labelled trend chart with a dotted threshold
// reference line, and a disabled-placeholder footer link until the
// drill-down ticket lands. Threshold colour is sourced from the Epic
// Linkage tier config via `getTierColorClass` (PRODOPSAI-375), so admin
// edits to the tier config in `/admin/metric-definitions` propagate here
// without code changes.
//
// PRODOPSAI-383 replaced the original 32px sparkline with the labelled
// chart; the threshold reference line that AC2.2.5 originally forbade was
// added back as part of that change. See the ticket for rationale.

import { Card, CardContent } from '@/components/ui';
import type { IEpicLinkageSection, IMetricThresholdSet } from '@skystone/shared';
import { useMetricDefinition } from '@/hooks/use-metric-definition';
import { getTierColorClass } from '@/lib/metric-color-class';
import { cn } from '@/lib/utils';
import { type IBucketAxisProps } from '@/lib/metric-view/bucket-format';
import { EpicLinkageTrendChart } from './EpicLinkageTrendChart';
import { useOpenDrill } from './drill/use-open-drill';

interface EpicLinkageCardProps {
  data: IEpicLinkageSection;
  /** Forwarded to `EpicLinkageTrendChart` — see `bucket-format.ts`. */
  axis?: IBucketAxisProps;
}

export function EpicLinkageCard({ data, axis }: Readonly<EpicLinkageCardProps>) {
  const definition = useMetricDefinition('epicLinkage');
  const openDrill = useOpenDrill();
  const thresholdSet: IMetricThresholdSet | null = definition
    ? { metricKey: definition.metricKey, direction: definition.direction, tiers: definition.tiers }
    : null;

  return (
    <Card className="h-full shadow-none">
      <CardContent className="space-y-3 p-4">
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Epic Linkage
        </p>
        <div className="flex items-baseline gap-2">
          <span
            className={cn('text-3xl font-semibold tabular-nums', getTierColorClass(data.linkagePercent, thresholdSet))}
          >
            {data.linkagePercent}%
          </span>
          <span className="text-xs text-muted-foreground">
            {data.inScopeCount.toLocaleString()} items
          </span>
        </div>

        {data.trend.length > 0 && <EpicLinkageTrendChart data={data.trend} axis={axis} />}

        <button
          type="button"
          onClick={() => openDrill('unlinked')}
          className="block text-left text-xs font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
        >
          View {data.unlinkedCount.toLocaleString()} unlinked items →
        </button>
      </CardContent>
    </Card>
  );
}
